import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
} from '@mui/material';
import { FC, JSXElementConstructor } from 'react';
import { LoadingOverlay } from '../common/LoadingOverlay';
import { PermissionsChecker } from '../PermissionsChecker/PermissionsChecker';
import { ArchiveGuard } from '../common/ArchiveGuard';

export type ContextMenuItem = {
  Icon: JSXElementConstructor<any>;
  label: string | JSX.Element;
  onClick: () => void;
  key?: string;
  isLoading?: boolean;
  permissionName?: string;
  shouldBeDisabledOnArchive?: boolean;
};
export type ContextMenuProps = {
  items: ContextMenuItem[];
};

const renderMenuItem = (
  key: string | undefined,
  label: string | JSX.Element,
  index: number,
  length: number,
  onClick: () => void,
  isLoading: boolean,
  Icon: JSXElementConstructor<any>,
) => {
  return (
    <MenuItem
      key={key || label.toString()}
      divider={index < length - 1}
      onClick={onClick}
      disabled={isLoading}
      data-testid={key}
    >
      <ListItemIcon>
        <Icon fontSize='small' />
      </ListItemIcon>
      <ListItemText
        disableTypography
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
      >
        {label}
      </ListItemText>
      <LoadingOverlay isLoading={isLoading} size={'1.5em'} />
    </MenuItem>
  );
};

export const ContextMenu: FC<ContextMenuProps> = ({ items }) => {
  return (
    <Paper elevation={10} sx={{ width: 'fit-content', maxWidth: '100%' }}>
      <MenuList
        variant='menu'
        sx={{ py: 0, display: 'flex', flexDirection: 'column' }}
      >
        {items.map(
          (
            {
              onClick,
              Icon,
              label,
              key,
              permissionName,
              isLoading = false,
              shouldBeDisabledOnArchive = false,
            },
            index,
            { length },
          ) => {
            const menuItem = renderMenuItem(
              key,
              label,
              index,
              length,
              onClick,
              isLoading,
              Icon,
            );

            const wrappedItem = shouldBeDisabledOnArchive ? (
              <ArchiveGuard key={key || index}>{menuItem}</ArchiveGuard>
            ) : (
              menuItem
            );

            return permissionName ? (
              <PermissionsChecker name={permissionName} key={key || index}>
                {wrappedItem}
              </PermissionsChecker>
            ) : (
              wrappedItem
            );
          },
        )}
      </MenuList>
    </Paper>
  );
};
